<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import Panzoom, { type PanzoomObject } from '@panzoom/panzoom'
import PhotoSwipe from 'photoswipe'
import type { ImageAnnotationMarker } from '@/domain/audience/types'
import { isSvgSource, nextPreviewRotation, resolveImagePreviewSize, rotatedFitRatio } from '@/shared/ui/imagePreview'

const props = defineProps<{
  src: string
  alt: string
  imageRatio?: string
  markers?: ImageAnnotationMarker[]
  mimeType?: string
  width?: number
  height?: number
  initialRotation?: number
  hint?: string
}>()
const emit = defineEmits<{ close: []; loadError: []; loadReady: []; selectMarker: [marker: ImageAnnotationMarker, previewRotation: number] }>()

const svgPreview = computed(() => isSvgSource(props.src, props.mimeType))
const inlineSvgPreview = computed(() => /^data:image\/svg\+xml(?:[;,]|$)/i.test(props.src.trim()))
const svgRoot = ref<HTMLElement | null>(null)
const svgStage = ref<HTMLElement | null>(null)
const svgCanvas = ref<HTMLElement | null>(null)
const svgContent = ref<HTMLElement | null>(null)
const svgStatus = ref<'loading' | 'ready' | 'error'>('loading')
const rotation = ref(Math.abs(Number(props.initialRotation || 0) % 180) === 90 ? 90 : 0)
let photoSwipe: PhotoSwipe | null = null
let previousOverflow = ''
let previousActive: HTMLElement | null = null
let resizeObserver: ResizeObserver | null = null
let svgPanzoom: PanzoomObject | null = null
let disposed = false
let forceCloseTimer = 0
let pendingMarker: { marker: ImageAnnotationMarker; rotation: number } | null = null

function requestRasterClose(preview: PhotoSwipe) {
  preview.close()
  window.clearTimeout(forceCloseTimer)
  forceCloseTimer = window.setTimeout(() => {
    if (photoSwipe === preview) preview.destroy()
  }, 450)
}

function syncRasterLayer(slide: NonNullable<PhotoSwipe['currSlide']>) {
  const layer = slide.container.querySelector<HTMLElement>('.vf-image-preview-hotspots')
  if (!layer) return
  const resolution = Math.max(0.01, Number(slide.currentResolution || slide.zoomLevels.initial) || 1)
  const transformScale = Math.max(0.01, Number(slide.currZoomLevel) / resolution || 1)
  layer.style.width = `${Math.round((Number(props.width) || slide.width) * resolution)}px`
  layer.style.height = `${Math.round((Number(props.height) || slide.height) * resolution)}px`
  layer.style.setProperty('--vf-preview-hotspot-size', `${44 / transformScale}px`)
}

function mountRasterMarkers(preview: PhotoSwipe, slide: NonNullable<PhotoSwipe['currSlide']>) {
  if (!props.markers?.length || slide.container.querySelector('.vf-image-preview-hotspots')) return
  const layer = document.createElement('div')
  layer.className = 'vf-image-preview-hotspots'
  props.markers.filter((marker) => marker.enabled !== false && Number.isFinite(Number(marker.x)) && Number.isFinite(Number(marker.y))).forEach((marker) => {
    const x = Math.min(1, Math.max(0, Number(marker.x)))
    const y = Math.min(1, Math.max(0, Number(marker.y)))
    const width = Number(marker.width)
    const height = Number(marker.height)
    const hasRegion = width > 0 && height > 0
    const button = document.createElement('button')
    button.type = 'button'
    button.className = `vf-image-preview-hotspot${hasRegion ? ' has-region' : ''}`
    button.style.left = `${x * 100}%`
    button.style.top = `${y * 100}%`
    if (hasRegion) {
      button.style.width = `${width * 100}%`
      button.style.height = `${height * 100}%`
    }
    button.setAttribute('aria-label', String(marker.name || '查看点位详情'))
    button.title = String(marker.name || '查看点位详情')
    button.addEventListener('pointerdown', (event) => event.stopPropagation())
    button.addEventListener('click', (event) => {
      event.preventDefault()
      event.stopPropagation()
      pendingMarker = { marker, rotation: rotation.value }
      requestRasterClose(preview)
    })
    layer.appendChild(button)
  })
  slide.container.appendChild(layer)
  syncRasterLayer(slide)
}

function applyRasterRotation(preview: PhotoSwipe, slide = preview.currSlide) {
  if (!slide) return
  const stageWidth = Number(slide.panAreaSize?.x) || preview.viewportSize.x
  const stageHeight = Number(slide.panAreaSize?.y) || preview.viewportSize.y
  const scale = rotation.value === 90 ? rotatedFitRatio(stageWidth, stageHeight, slide.width, slide.height) : 1
  slide.container.style.setProperty('--vf-preview-rotation', `${rotation.value}deg`)
  slide.container.style.setProperty('--vf-preview-rotation-scale', String(scale))
  const button = preview.element?.querySelector<HTMLElement>('.pswp__vf-preview-rotate')
  button?.setAttribute('aria-pressed', String(rotation.value === 90))
}

async function openRasterPreview() {
  const size = await resolveImagePreviewSize(props.src, props.width, props.height)
  if (disposed || svgPreview.value) return
  let loadSettled = false
  const preview = new PhotoSwipe({
    dataSource: [{ src: props.src, width: size.width, height: size.height, alt: props.alt }],
    index: 0,
    loop: false,
    allowPanToNext: false,
    wheelToZoom: true,
    bgOpacity: 0.96,
    showHideAnimationType: 'fade',
    initialZoomLevel: 'fit',
    secondaryZoomLevel: 2.5,
    maxZoomLevel: 5,
    imageClickAction: 'zoom',
    tapAction: 'zoom',
    doubleTapAction: 'zoom',
    closeTitle: '关闭图片预览',
    zoomTitle: '缩放图片',
    errorMsg: '图片加载失败',
    close: false,
    arrowPrev: false,
    arrowNext: false,
    counter: false,
    mainClass: 'vf-photo-preview',
  })
  photoSwipe = preview
  preview.on('uiRegister', () => {
    if (props.hint) {
      preview.ui?.registerElement({
        name: 'vf-preview-hint',
        className: 'pswp__vf-preview-hint',
        order: 8,
        isButton: false,
        appendTo: 'root',
        html: '',
        onInit: (element) => { element.textContent = props.hint || '' },
      })
    }
    preview.ui?.registerElement({
      name: 'vf-preview-close',
      className: 'pswp__vf-preview-close',
      order: 20,
      isButton: true,
      appendTo: 'bar',
      title: '关闭图片预览',
      html: '<span aria-hidden="true">×</span>',
      onClick: () => requestRasterClose(preview),
    })
    preview.ui?.registerElement({
      name: 'vf-preview-rotate',
      className: 'pswp__vf-preview-rotate',
      order: 9,
      isButton: true,
      appendTo: 'root',
      title: '旋转图片',
      html: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11a8 8 0 1 1-2.34-5.66L20 7.68M20 3v4.68h-4.68" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg><span>旋转图片</span>',
      onInit: (element) => element.setAttribute('aria-pressed', String(rotation.value === 90)),
      onClick: (_event, _element, instance) => {
        rotation.value = nextPreviewRotation(rotation.value)
        const slide = instance.currSlide
        if (slide) slide.zoomTo(slide.zoomLevels.initial, instance.getViewportCenterPoint(), 0)
        applyRasterRotation(instance, slide)
      },
    })
  })
  preview.on('afterSetContent', ({ slide }) => { applyRasterRotation(preview, slide); mountRasterMarkers(preview, slide) })
  preview.on('loadComplete', ({ isError }) => {
    if (loadSettled) return
    loadSettled = true
    if (isError) emit('loadError')
    else emit('loadReady')
  })
  preview.on('afterInit', () => { if (preview.currSlide) mountRasterMarkers(preview, preview.currSlide) })
  preview.on('zoomPanUpdate', ({ slide }) => syncRasterLayer(slide))
  preview.on('resolutionChanged', () => { if (preview.currSlide) syncRasterLayer(preview.currSlide) })
  preview.on('resize', () => { if (preview.currSlide) applyRasterRotation(preview, preview.currSlide) })
  preview.on('close', () => {
    window.clearTimeout(forceCloseTimer)
    forceCloseTimer = window.setTimeout(() => { if (photoSwipe === preview) preview.destroy() }, 450)
  })
  preview.on('destroy', () => {
    window.clearTimeout(forceCloseTimer)
    photoSwipe = null
    if (!disposed) {
      const selection = pendingMarker
      pendingMarker = null
      if (selection) emit('selectMarker', selection.marker, selection.rotation)
      emit('close')
    }
  })
  preview.init()
}

async function fitSvgToStage() {
  if (!svgStage.value || !svgContent.value) return
  const size = await resolveImagePreviewSize(props.src, props.width, props.height)
  if (disposed || !svgStage.value || !svgContent.value) return
  const rect = svgStage.value.getBoundingClientRect()
  if (!rect.width || !rect.height) return
  const fit = Math.min(rect.width / size.width, rect.height / size.height)
  const width = size.width * fit
  const height = size.height * fit
  svgContent.value.style.width = `${width}px`
  svgContent.value.style.height = `${height}px`
  const scale = rotation.value === 90 ? Math.min(1, rect.width / height, rect.height / width) : 1
  svgContent.value.style.setProperty('--vf-preview-rotation', `${rotation.value}deg`)
  svgContent.value.style.setProperty('--vf-preview-rotation-scale', String(scale))
}

function setupSvgPanzoom() {
  if (!svgCanvas.value || !svgStage.value || svgPanzoom) return
  svgPanzoom = Panzoom(svgCanvas.value, {
    animate: false,
    canvas: true,
    contain: 'outside',
    cursor: 'grab',
    maxScale: 5,
    minScale: 1,
    panOnlyWhenZoomed: true,
    pinchAndPan: true,
    step: 0.35,
    touchAction: 'none',
  })
  svgStage.value.addEventListener('wheel', svgPanzoom.zoomWithWheel, { passive: false })
}

function zoomSvgIn() {
  svgPanzoom?.zoomIn({ animate: true })
}

function zoomSvgOut() {
  svgPanzoom?.zoomOut({ animate: true })
}

function resetSvgView() {
  svgPanzoom?.reset({ animate: true })
}

function rotateSvg() {
  rotation.value = nextPreviewRotation(rotation.value)
  void fitSvgToStage()
}

function close() {
  if (photoSwipe) requestRasterClose(photoSwipe)
  else emit('close')
}

function trapSvgFocus(event: KeyboardEvent) {
  if (event.key === 'Escape') { close(); return }
  if (event.key !== 'Tab') return
  const controls = [...(svgRoot.value?.querySelectorAll<HTMLElement>('button:not([disabled])') || [])]
  const first = controls[0]
  const last = controls[controls.length - 1]
  if (!first || !last) return
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
}

onMounted(() => {
  previousActive = document.activeElement as HTMLElement | null
  if (!svgPreview.value) { void openRasterPreview(); return }
  previousOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'
  window.addEventListener('keydown', trapSvgFocus)
  void nextTick(() => {
    void fitSvgToStage().then(setupSvgPanzoom)
    resizeObserver = typeof ResizeObserver === 'function' && svgStage.value ? new ResizeObserver(() => { void fitSvgToStage() }) : null
    if (resizeObserver && svgStage.value) resizeObserver.observe(svgStage.value)
    svgRoot.value?.querySelector<HTMLElement>('[data-svg-close]')?.focus()
  })
})

onBeforeUnmount(() => {
  disposed = true
  window.clearTimeout(forceCloseTimer)
  if (photoSwipe) { photoSwipe.destroy(); photoSwipe = null }
  resizeObserver?.disconnect()
  if (svgPanzoom && svgStage.value) svgStage.value.removeEventListener('wheel', svgPanzoom.zoomWithWheel)
  svgPanzoom?.destroy()
  svgPanzoom = null
  window.removeEventListener('keydown', trapSvgFocus)
  if (svgPreview.value) document.body.style.overflow = previousOverflow
  previousActive?.focus()
})
</script>

<template>
  <Teleport v-if="svgPreview" to="body">
    <section ref="svgRoot" class="vf-svg-preview" :class="`is-${svgStatus}`" role="dialog" aria-modal="true" :aria-label="alt">
      <div ref="svgStage" class="vf-svg-preview-stage">
        <div ref="svgCanvas" class="vf-svg-preview-canvas">
          <div ref="svgContent" class="vf-svg-preview-content"><img v-if="inlineSvgPreview" :src="src" :alt="alt" @load="svgStatus = 'ready'; emit('loadReady')" @error="svgStatus = 'error'; emit('loadError')" /><object v-else type="image/svg+xml" :data="src" :aria-label="alt" @load="svgStatus = 'ready'; emit('loadReady')" @error="svgStatus = 'error'; emit('loadError')"></object></div>
        </div>
        <div class="vf-svg-preview-loading" role="status" aria-live="polite"><i v-if="svgStatus !== 'error'" aria-hidden="true"></i><span>{{ svgStatus === 'error' ? 'SVG 加载失败，请关闭后重试' : 'SVG 加载中' }}</span></div>
      </div>
      <header class="vf-svg-preview-toolbar"><span>{{ alt || 'SVG 大图' }}</span><button type="button" data-svg-close aria-label="关闭 SVG 预览" @click="close">×</button></header>
      <p v-if="hint" class="vf-svg-preview-hint">{{ hint }}</p>
      <div class="vf-svg-preview-actions">
        <button class="vf-svg-preview-zoom" type="button" aria-label="缩小图片" @click="zoomSvgOut">−</button>
        <button class="vf-svg-preview-zoom" type="button" aria-label="重置图片位置和缩放" @click="resetSvgView">复位</button>
        <button class="vf-svg-preview-zoom" type="button" aria-label="放大图片" @click="zoomSvgIn">＋</button>
        <button class="vf-svg-preview-rotate" type="button" :aria-pressed="rotation === 90" aria-label="旋转图片" @click="rotateSvg"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11a8 8 0 1 1-2.34-5.66L20 7.68M20 3v4.68h-4.68" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg><span>旋转图片</span></button>
      </div>
    </section>
  </Teleport>
</template>
